/**
 * PermissionRoute Component
 *
 * Advanced route-level permission protection that redirects unauthorized users.
 * Works with React Router for page-level access control based on permissions.
 */

import type React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import type { PermissionCheck } from '@/types/permissions';

interface PermissionRouteProps {
  children: React.ReactNode;

  // Permission check configuration
  permission?: PermissionCheck;
  permissions?: PermissionCheck[];

  // Logic for multiple permissions
  requireAll?: boolean; // true = AND logic, false = OR logic (default: false)

  // Custom permission check function (overrides permission/permissions)
  hasAccess?: () => boolean;

  // Redirect configuration
  redirectTo?: string; // Default: '/'
  redirectToLogin?: boolean; // Redirect to login if not authenticated (default: false)

  // Loading state handling
  showLoadingDuringCheck?: boolean; // Show loading while checking permissions (default: true)
  loadingComponent?: React.ReactNode;
}

/**
 * PermissionRoute - Protect routes based on permissions
 *
 * @example
 * // Single permission check
 * <PermissionRoute permission={COMMON_CHECKS.ADMIN}>
 *   <AdminPanel />
 * </PermissionRoute>
 *
 * @example
 * // Multiple permissions with AND logic
 * <PermissionRoute
 *   permissions={[COMMON_CHECKS.ADMIN, COMMON_CHECKS.VIEWER]}
 *   requireAll={true}
 *   redirectTo="/dashboard"
 * >
 *   <ProtectedPage />
 * </PermissionRoute>
 */
export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  hasAccess,
  redirectTo = '/',
  redirectToLogin = false,
  showLoadingDuringCheck = true,
  loadingComponent,
}) => {
  const { user, isLoading: authLoading } = useAuth();
  const {
    checkPermissionWithLoading,
    checkPermissions,
    isLoading: permissionsLoading,
  } = usePermissions();
  const location = useLocation();

  // Handle authentication loading
  if (authLoading) {
    if (showLoadingDuringCheck && loadingComponent) {
      return <>{loadingComponent}</>;
    }
    return <div>Loading...</div>;
  }

  // Not authenticated - redirect to login if specified
  if (!user) {
    if (redirectToLogin) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Handle permissions loading
  if (showLoadingDuringCheck && permissionsLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    return <div>Checking permissions...</div>;
  }

  // Custom permission check overrides everything
  if (hasAccess) {
    const hasAccessResult = hasAccess();
    if (!hasAccessResult) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{children}</>;
  }

  // Single permission check
  if (permission) {
    const { hasPermission, isLoading: permissionLoading } = checkPermissionWithLoading(permission);

    // Let NProgress handle loading state instead of showing text
    if (showLoadingDuringCheck && permissionLoading) {
      if (loadingComponent) {
        return <>{loadingComponent}</>;
      }
      return null; // Let NProgress show loading, don't render anything
    }

    if (!hasPermission && !permissionLoading) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{children}</>;
  }

  // Multiple permission checks
  if (permissions && permissions.length > 0) {
    const results = checkPermissions(permissions);
    const permissionValues = Object.values(results);

    // If permissions are still loading globally, let NProgress handle it
    // Don't redirect while global permission loading is happening
    if (permissionsLoading) {
      if (loadingComponent) {
        return <>{loadingComponent}</>;
      }
      return null; // Let NProgress show loading, don't render anything
    }

    const hasAccess = requireAll
      ? permissionValues.every(Boolean) // AND logic - all must be true
      : permissionValues.some(Boolean); // OR logic - at least one must be true

    if (!hasAccess) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{children}</>;
  }

  // If no permission checks are provided, fail-closed — redirect to safety
  console.warn('PermissionRoute: No permission checks provided, redirecting (fail-closed)');
  return <Navigate to={redirectTo} replace />;
};

// Convenience components for common route protection patterns

/**
 * AdminRoute - Only allow access to users with system admin permissions
 */
export const AdminRoute: React.FC<{
  children: React.ReactNode;
  redirectTo?: string;
  loadingComponent?: React.ReactNode;
}> = ({ children, redirectTo = '/', loadingComponent }) => {
  const { isAdmin } = usePermissions();

  return (
    <PermissionRoute
      hasAccess={isAdmin}
      redirectTo={redirectTo}
      loadingComponent={loadingComponent}
    >
      {children}
    </PermissionRoute>
  );
};
